/* eslint-disable camelcase, indent */
import { useState, useRef } from "react";
import {
  Create,
  Edit,
  SimpleForm,
  ReferenceInput,
  FormDataConsumer,
  required,
  minValue,
} from "react-admin";
import { useWatch, useFormContext } from "react-hook-form";
import Divider from "@mui/material/Divider";
import FormHelperText from "@mui/material/FormHelperText";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { parse } from "query-string";
import pick from "lodash/pick";
import isEmpty from "lodash/isEmpty";
import { useLocation } from "react-router-dom";

import resources from "../../constants/resources";
import {
  CONTENT_ITEM_CREATE_TITLE,
  CONTENT_ITEM_EDIT_TITLE,
} from "../../constants/titleConstants";
import {
  SURVEY_CONTENT_ITEM_TYPE_LIST,
  QUIZ_TUTORIAL_CONTENT_ITEM_TYPE_LIST,
  CONTENT_TYPES,
  CONTENT_ITEM_TYPES,
  CONTENT_ITEM_TYPE_RENDERER,
  CONTENT_ITEM_TYPE_ES_RENDERER,
} from "../../constants/contentConstants";
import { CONTENT_ITEM_HELPER_TEXT } from "../../constants/helperTextConstants";
import {
  AutoCompleteInputField,
  NumberInputField,
  SelectInputField,
  TextInputField,
} from "../../styles/commonStyles";
import CustomToolbar from "../common/CustomToolbar";
import { getNonNullishValues } from "../../utils/InputUtils";

const ContentItemForm = ({
  contentId,
  order,
  isEdit,
  isExpandMode,
  contentType,
  redirect,
}) => {
  const contentItemType = useRef();
  const [shouldPopulateTranslations, setShouldPopulateTranslations] =
    useState(false);

  return (
    <SimpleForm
      sanitizeEmptyValues
      toolbar={<CustomToolbar redirect={redirect} />}
      defaultValues={{ content: contentId, order, point_value: 0 }}
    >
      {isEdit && <TextInputField InputProps={{ disabled: true }} source="id" />}
      <ReferenceInput
        label={resources.CONTENT.label}
        source="content"
        reference={resources.CONTENT.url}
      >
        <AutoCompleteInputField
          optionText="name_en"
          validate={required()}
          helperText={CONTENT_ITEM_HELPER_TEXT.content}
        />
      </ReferenceInput>
      <SelectInputField
        source="content_item_type"
        validate={required()}
        choices={
          contentType === CONTENT_TYPES.survey.id
            ? SURVEY_CONTENT_ITEM_TYPE_LIST(false)
            : QUIZ_TUTORIAL_CONTENT_ITEM_TYPE_LIST(false)
        }
        helperText={CONTENT_ITEM_HELPER_TEXT.type}
      />
      {!isExpandMode && !isEdit && (
        <NumberInputField
          label="Order"
          source="order"
          min="0"
          validate={required()}
          helperText={CONTENT_ITEM_HELPER_TEXT.order}
        />
      )}
      <PointsInput />
      <FormDataConsumer fullWidth>
        {({ formData, ...rest }) => {
          const { reset } = useFormContext();
          if (formData.content_item_type !== contentItemType.current) {
            reset(formData, { keepIsValid: true });
            contentItemType.current = formData.content_item_type;
          }
          return formData.content_item_type &&
            CONTENT_ITEM_TYPE_RENDERER[formData.content_item_type] ? (
            // eslint-disable-next-line react/jsx-indent
            <>
              <Divider light={false} />
              <FormHelperText id="english-form-helper-text">
                <b>English Fields</b>
              </FormHelperText>
              {CONTENT_ITEM_TYPE_RENDERER[
                formData.content_item_type
              ].renderFunction({ rest, formData })}
            </>
          ) : null;
        }}
      </FormDataConsumer>
      <FormDataConsumer fullWidth>
        {({ formData, ...rest }) =>
          formData.content_item_type &&
          (CONTENT_ITEM_TYPE_ES_RENDERER[formData.content_item_type] ? (
            <>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    onChange={e => {
                      setShouldPopulateTranslations(e.target.checked);
                    }}
                    name="add-translations"
                    disabled={formData.content_item_json_i18n_es}
                    defaultChecked={formData.content_item_json_i18n_es}
                  />
                }
                label="Add Translation Fields"
              />
              {(formData.content_item_json_i18n_es && isEdit) ||
              shouldPopulateTranslations ? (
                <>
                  <Divider light />
                  <FormHelperText id="spanish-form-helper-text">
                    <b>Spanish Fields</b>
                  </FormHelperText>
                  {CONTENT_ITEM_TYPE_ES_RENDERER[
                    formData.content_item_type
                  ].renderFunction({ rest, formData })}
                </>
              ) : null}
            </>
          ) : null)
        }
      </FormDataConsumer>
    </SimpleForm>
  );
};

const transformSubmissionData = data => {
  const {
    content_item_json_i18n_en,
    content_item_json_i18n_es,
    content_item_type: contentItemType,
  } = data;
  let contentItemJsonEN = content_item_json_i18n_en;
  let contentItemJsonES = content_item_json_i18n_es;
  if (
    !isEmpty(CONTENT_ITEM_TYPE_RENDERER[contentItemType].questionValuesToPick)
  ) {
    const { question } = contentItemJsonEN;
    contentItemJsonEN = {
      ...contentItemJsonEN,
      question: pick(
        question,
        CONTENT_ITEM_TYPE_RENDERER[contentItemType].questionValuesToPick
      ),
    };
    if (!isEmpty(contentItemJsonES)) {
      // eslint-disable-next-line no-shadow
      const { question } = contentItemJsonES;
      contentItemJsonES = {
        ...contentItemJsonES,
        question: pick(
          question,
          CONTENT_ITEM_TYPE_RENDERER[contentItemType].questionValuesToPick
        ),
      };
    }
  }
  return getNonNullishValues({
    ...data,
    content_item_json_i18n_en: JSON.stringify(
      pick(
        contentItemJsonEN,
        CONTENT_ITEM_TYPE_RENDERER[contentItemType].valuesToPick
      )
    ),
    ...(!isEmpty(contentItemJsonES) && {
      content_item_json_i18n_es: JSON.stringify(
        pick(
          contentItemJsonES,
          CONTENT_ITEM_TYPE_ES_RENDERER[contentItemType].valuesToPick
        )
      ),
    }),
  });
};

const ContentItemCreate = props => {
  const { search } = useLocation();
  const {
    content_id: contentIdString,
    order: orderString,
    ct: contentTypeString,
  } = parse(search);
  const contentId = contentIdString ? parseInt(contentIdString, 10) : "";
  const order = orderString ? parseInt(orderString, 10) : "";
  const redirect = contentId
    ? `/${resources.CONTENT.url}/${contentId}/content_items`
    : false;
  const contentType =
    contentTypeString === CONTENT_TYPES.survey.queryParam
      ? CONTENT_TYPES.survey.id
      : CONTENT_TYPES.tutorial.id;

  return (
    <Create
      {...props}
      title={CONTENT_ITEM_CREATE_TITLE}
      transform={transformSubmissionData}
      redirect={redirect}
    >
      {ContentItemForm({
        ...props,
        contentId,
        order,
        contentType,
      })}
    </Create>
  );
};

const ContentItemEdit = props => {
  const {
    isExpandMode,
    contentTypeQueryParam: contentTypeQueryParamProp,
    record,
  } = props;
  const { search } = useLocation();
  const { ct: contentTypeQueryParam, cid: contentId } = parse(search);
  const contentTypeInput = contentTypeQueryParam || contentTypeQueryParamProp;
  const contentTypeValue =
    contentTypeInput === CONTENT_TYPES.survey.queryParam
      ? CONTENT_TYPES.survey.id
      : CONTENT_TYPES.tutorial.id;

  const redirect =
    isExpandMode && record && record.content
      ? `/content-admin/content/${record.content}/content_items`
      : `/content-admin/content/${contentId}/content_items`;

  return (
    <Edit
      {...props}
      title={!isExpandMode ? CONTENT_ITEM_EDIT_TITLE : " "}
      transform={transformSubmissionData}
      mutationMode="pessimistic"
      redirect={redirect}
      actions={false}
    >
      {ContentItemForm({
        ...props,
        isEdit: true,
        isExpandMode,
        contentType: contentTypeValue,
        redirect,
      })}
    </Edit>
  );
};

const PointsInput = () => {
  const contentItemType = useWatch({ name: "content_item_type" });
  const min =
    contentItemType ===
    CONTENT_ITEM_TYPES.MultipleChoiceQuestionWithCorrectAnswer.id
      ? 1
      : 0;
  return (
    <NumberInputField
      label="Point Value"
      source="point_value"
      min={min}
      validate={[
        required(),
        minValue(min, `Point Value should be greater than ${min - 1}`),
      ]}
      InputProps={{
        disabled: CONTENT_ITEM_TYPE_RENDERER[contentItemType]
          ? !CONTENT_ITEM_TYPE_RENDERER[contentItemType].isPointsEnabled
          : true,
      }}
      helperText={
        contentItemType ===
        CONTENT_ITEM_TYPES.MultipleChoiceQuestionWithCorrectAnswer.id
          ? CONTENT_ITEM_HELPER_TEXT.points
          : " "
      }
    />
  );
};

export { ContentItemEdit, ContentItemCreate };
